/**
 * 전자 서명 링크 컴포넌트
 * 계약서의 전자 서명 상태를 관리하고 표시하는 컴포넌트입니다.
 * 전자 서명 문서의 생성, 취소, 폐기 등의 기능을 제공합니다.
 */

import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { DetailTotalData, ESignListDetail, EsignType } from "src/api/contract/contract-types";
import { BaseButton, BaseInput, BaseModal, BaseRadio } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { ViewYmdFormat } from "src/utils";
import useEsignApi from "../../hooks/useEsignApi";
import MakeEsignInfo from "../modal/MakeEsignInfo";

/**
 * 전자 서명 링크 컴포넌트의 props 인터페이스
 */
interface ContractBasicProp {
  detailData: DetailTotalData; // 상품 상세 조회 정보
  setEditable?: any; // 편집 모드 설정 함수
  viewOnly?: boolean; // 읽기 전용 모드 여부
  setData: any; // 데이터 업데이트 함수
  initMainData?: any; // 초기 데이터 설정 함수
  setIsEsign?: any; // 전자 서명 상태 설정 함수
  data?: any; // 추가 데이터
}

/**
 * 전자 서명 문서 테이블 컬럼 정의
 */
const columns: any = [
  {
    Header: "문서 이름",
    accessor: "workflowName",
    Cell: ({ value }: any) => {
      return (
        <div className={"w-100"}>
          <p className="text-left">{value && value}</p>
        </div>
      );
    },
    width: 250,
  },
  {
    Header: "상태",
    accessor: "status",
    Cell: ({ value, row, setData, viewOnly, setEditable }: any) => {
      const { cancelEsignData } = useEsignApi();
      const [isDelete, setIsDelete] = useState(false); // 삭제 모달 표시 여부
      const [reason, setReason] = useState(""); // 취소/폐기 사유
      const [message, setMessage] = useState(""); // 상태 변경 메시지
      const [title, setTitle] = useState(""); // 모달 제목
      const [isPop, setIsPop] = useState(false); // 팝업 표시 여부
      const [reasonFlag, setReasonFlag] = useState(true); // 사유 직접 입력 여부

      /**
       * 문서 상태에 따른 제목과 기본 사유 설정
       */
      useEffect(() => {
        if (row.original.status) {
          if (row.original.status === EsignType.Playing) {
            setTitle("취소");
          }
          if (row.original.status === EsignType.Canceled) {
            setTitle("삭제");
          }
          if (row.original.status === EsignType.Truncate) {
            setTitle("폐기");
          }
          setReason("내용 오류로 인한 " + title);
        }
      }, [row, setTitle, setReason]);

      /**
       * 문서 상태 변경 함수
       * @param reason 변경 사유
       */
      const cancelDocument = useCallback(
        async (reason: string) => {
          let nextStatus = "";
          if (value === EsignType.Playing) {
            nextStatus = "CANCEL";
          }
          if (value === EsignType.Canceled) {
            nextStatus = "DELETE";
          }
          if (value === EsignType.Truncate) {
            nextStatus = "DISPOSAL";
          }
          const sendData = {
            contractId: row.original.contractId,
            workflowId: row.original.workflowId,
            commandType: nextStatus,
            comment: reason,
          };
          const response = await cancelEsignData(sendData);
          if (response.data.data.message) {
            setMessage("");
            setIsPop(true);
            setEditable(false);
            setData();
          }
          setIsDelete(false);
        },
        [cancelEsignData, row, setData, value],
      );

      return (
        <>
          {isPop && (
            <BaseModal isOpen={isPop} title="문서 상태가 변경되었습니다." btnRightTitle="확인" onClick={() => setIsPop(false)}>
              {message ? <p>{message}</p> : ""}
            </BaseModal>
          )}
          <div>
            {value === EsignType.Complete && <span className={`${value === EsignType.Complete && "chip green"}`}>완료</span>}
            {value === EsignType.Canceled && (
              <div className="flex-row flex-center-start">
                <span className={`${value === EsignType.Canceled && "chip gray"}`}>취소</span>
              </div>
            )}
            {value === EsignType.Disposal && (
              <div className="flex-row flex-center-start">
                <span className={`${value === EsignType.Disposal && "chip red"}`}>폐기</span>
              </div>
            )}
            {value === EsignType.Playing && (
              <div className="flex-row flex-center-start">
                <span className={`${value === EsignType.Playing && "chip blue"}`}>진행중</span>
                {viewOnly === false && (
                  <BaseButton
                    className="color-white ml15"
                    title={"문서 " + title}
                    onClick={() => {
                      setIsDelete(true);
                    }}
                    disabled={value !== EsignType.Playing}
                  />
                )}
              </div>
            )}
            {value === EsignType.Truncate && (
              <div className="flex-row flex-center-start">
                <span className={`${value === EsignType.Truncate && "chip red"}`}>완전삭제</span>
              </div>
            )}
          </div>
          {isDelete && (
            <BaseAbstractModal isOpen={isDelete} size={"medium"} className="dialog-modal ">
              <section className="base-abstract-modal__title flex-center-between ">
                <h1>문서 {title}</h1>
              </section>
              <section className="base-abstract-modal__contents mx30">
                <ul className="base-list">
                  <li>문서를 {title}하시면 복구할 수 없습니다.</li>
                </ul>
                <div className="contents-container mt20">
                  <section className="contents-container__grid my0">
                    <div className="contents-container__grid-index">
                      <p>문서명</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <BaseInput disabled value={row.original.workflowName} />
                    </div>
                  </section>

                  <section className="contents-container__grid mb0">
                    <div className="contents-container__grid-index">
                      <p className="">{title} 사유</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <BaseRadio
                        id="reasonType1"
                        name="reasonType"
                        className="mr10"
                        value={"true"}
                        label={"내용 오류로 인한 " + title}
                        checked={String(reasonFlag) === "true" ? true : false}
                        onChange={() => {
                          setReasonFlag(true);
                          setReason("내용 오류로 인한 " + title);
                        }}
                      ></BaseRadio>
                      <div className="minmax300 font14">
                        <BaseRadio
                          id="reasonType2"
                          name="reasonType"
                          value={"false"}
                          label="직접 입력"
                          checked={String(reasonFlag) === "false" ? true : false}
                          onChange={() => {
                            setReasonFlag(false);
                            setReason("");
                          }}
                        ></BaseRadio>
                        {reasonFlag === false && (
                          <BaseInput
                            maxLength={250}
                            name="reason"
                            className="mt10"
                            placeholder={title + " 사유를 입력하세요(250자 이내)"}
                            value={reason}
                            onChange={(value: string) => {
                              setReason(value);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </section>
              <section className="base-abstract-modal__btn-wrap">
                <BaseButton
                  title={title + " 안함"}
                  className="color-white"
                  onClick={() => {
                    setIsDelete(false);
                  }}
                />
                <BaseButton
                  title={title}
                  className="color-white"
                  onClick={() => {
                    cancelDocument(reason);
                  }}
                />
              </section>
            </BaseAbstractModal>
          )}
        </>
      );
    },
    width: 250,
  },

  {
    Header: "수정일시",
    accessor: "modifiedDate",
    width: 220,
    Cell: ({ value, row }: any) => {
      return <>{value && moment(value).subtract(9, "hours").format(ViewYmdFormat.FULL)}</>;
    },
  },
  {
    Header: "문서 내용 확인",
    accessor: "edit",
    width: 300,
    Cell: ({ row, detailData, getDetail }: any) => {
      const [, setDetail] = useState<ESignListDetail>();

      /**
       * 진행 중인 문서 조회
       * 문서의 상세 정보를 가져와 새 창에서 표시합니다.
       */
      const clickPlayingDocument = useCallback(async () => {
        const dt = await getDetail(detailData.contract.contractId, row.original.workflowId);
        if (dt) {
          setDetail(dt);
          window.open(dt.downloadUrl, "_blank");
        }
      }, [detailData.contract.contractId, getDetail, row.original.workflowId]);

      /**
       * 인증서 문서 조회
       * 문서의 인증서를 가져와 새 창에서 표시합니다.
       */
      const clickCertDocument = useCallback(async () => {
        const dt = await getDetail(detailData.contract.contractId, row.original.workflowId);
        if (dt) {
          setDetail(dt);
          window.open(dt.certificateUrl, "_blank");
        }
      }, [detailData.contract.contractId, getDetail, row.original.workflowId]);

      return (
        <>
          <div className="flex-row flex-center-start">
            <BaseButton
              className="color-white mr10"
              title="문서"
              onClick={() => clickPlayingDocument()}
              disabled={EsignType.Complete !== row.original.status}
            />
            <BaseButton
              className="color-white"
              title="이력 인증서"
              onClick={() => clickCertDocument()}
              disabled={EsignType.Complete !== row.original.status}
            />
          </div>
        </>
      );
    },
  },
];

/**
 * 전자 서명 링크 컴포넌트
 * @param detailData 계약 상세 정보
 * @param viewOnly 읽기 전용 모드 여부
 * @param setData 데이터 업데이트 함수
 * @param setEditable 편집 모드 설정 함수
 * @param initMainData 초기 데이터 설정 함수
 * @param setIsEsign 전자 서명 상태 설정 함수
 * @param data 추가 데이터
 */
const EsignOnLink = ({ detailData, viewOnly, setData, setEditable, initMainData, setIsEsign, data }: ContractBasicProp) => {
  const { getList, getDetail } = useEsignApi();
  const [eSignData, setEsignData] = useState<Array<ESignListDetail>>([]); // 전자 서명 문서 목록
  const [eSignflags, setESignFlags] = useState(false); // 전자 서명 플래그
  const [isSaved, setIsSaved] = useState(false); // 저장 상태
  const [origin, setOrigin] = useState<any>(); // 원본 데이터
  const [isLoading, setIsLoading] = useState(false); // API 로딩 상태
  const initialLoadRef = useRef(false); // 최초 로딩 여부를 추적하는 ref

  /**
   * 테이블 설정
   * react-table 훅을 사용하여 테이블 기능을 설정합니다.
   */
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: eSignData,
      initialState: { pageSize: 1000 },
      detailData,
      getDetail,
      setData,
      viewOnly,
      setEditable,
    },
    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  /**
   * 테이블 너비 계산
   * 각 컬럼의 너비를 합산하여 전체 테이블 너비를 계산합니다.
   */
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  /**
   * 초기 데이터 로드
   * 계약 ID에 해당하는 전자 서명 문서 목록을 가져옵니다.
   */
  const initDatas = useCallback(async () => {
    // 이미 로딩 중이거나 계약 ID가 없으면 중단
    if (isLoading || !detailData?.contract?.contractId) return;

    // 이미 데이터가 로드되었고, 배열이 비어있지 않으면 중단
    if (initialLoadRef.current && eSignData.length > 0) return;

    setIsLoading(true);
    try {
      const list = await getList(Number(detailData.contract.contractId));

      if (Array.isArray(list)) {
        const rv = _.sortBy(list, "modifiedDate").reverse();
        setEsignData(rv);
      } else {
        setEsignData([]);
      }

      if (data) {
        setOrigin(data);
      }

      // 로드 완료 플래그 설정
      initialLoadRef.current = true;
    } catch (error) {
      console.error("전자서명 목록 조회 오류:", error);
      setEsignData([]);
    } finally {
      setIsLoading(false);
    }
  }, [detailData?.contract?.contractId, getList, data, isLoading, eSignData.length]);

  /**
   * 전자 서명 플래그 변경 시 데이터 업데이트
   */
  useEffect(() => {
    if (eSignflags === false && setData && origin && !_.isEqual(data, origin)) {
      setData(origin);
    }
  }, [eSignflags, setData, origin]);

  /**
   * 상세 정보 변경 시 데이터 초기화
   */
  useEffect(() => {
    // 이미 로드되었으면 스킵
    if (initialLoadRef.current) return;

    // 계약 ID가 있을 때만 한 번 실행
    if (detailData?.contract?.contractId) {
      initDatas();
    }
  }, [initDatas]);

  return (
    <>
      <div className="flex-start">
        <section className="contents-container__grid">
          <div className="contents-container__grid-index">
            <p className={viewOnly ? "" : "required"}>계약서</p>
          </div>
          {viewOnly === false && (
            <div className="contents-container__grid-contents">
              <BaseButton
                className="color-white"
                title="전자서명 바로가기"
                onClick={() => {
                  if (!isLoading) setESignFlags(true);
                }}
                disabled={isLoading}
              />
              {eSignflags && !isLoading && (
                <MakeEsignInfo
                  setEditable={setEditable}
                  setIsSaved={setIsSaved}
                  flags={eSignflags}
                  setESignFlags={setESignFlags}
                  initDatas={initDatas}
                  initMainData={initMainData}
                  setIsEsign={setIsEsign}
                  setData={setData}
                />
              )}
            </div>
          )}
        </section>
      </div>
      <div className="flex-row flex-start">
        <section className="contents-container__grid">
          <div className="contents-container__grid-index"></div>
          <div className="contents-container__grid-contents">
            <div className="flex-row d-flex flex-center-start">
              <div {...getTableProps()} className="base-table sticky">
                <div className="header">
                  {headerGroups.map((headerGroup) => (
                    <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                      {headerGroup.headers.map((header) => {
                        return (
                          <div {...header.getHeaderProps()} className="base-table__th">
                            {header.render("Header")}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div {...getTableBodyProps()} className="body">
                  {rows.map((row: any) => {
                    prepareRow(row);

                    return (
                      <div {...row.getRowProps()} className={`base-table__tr`}>
                        {row.cells &&
                          row.cells.map((cell: any, index: number) => {
                            // console.log(row.original, user);
                            // const visibleFlag = row.original.createdBy === user?.email ? false : true;

                            return (
                              <div {...cell.getCellProps()} className="base-table__td">
                                {cell.render("Cell")}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                  <div>
                    {rows.length === 0 && (
                      <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                        <div className="base-table__td w-100 text-center">
                          <div className="w-100">{<span>데이터가 없습니다.</span>}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {isSaved && (
        <BaseModal
          isOpen={isSaved}
          btnRightTitle="확인"
          title={"전송 되었습니다."}
          onClick={() => {
            setIsSaved(false);
          }}
        ></BaseModal>
      )}
    </>
  );
};
export default EsignOnLink;
